import type { IDetectedBarcode, IScannerError } from "@yudiel/react-qr-scanner"
import { useCallback, useEffect, useReducer, useRef } from "react"

import type { CameraPermission, CodeScannerErrorKind, CodeScannerStatus, ScannedCode } from "./CodeScanner.types"
import prepareCodeDecoder from "./zxingModule"

// A code held in front of the camera decodes on every frame; one read per code is enough until this has passed
const SAME_CODE_COOLDOWN_MS = 2000

// Errors nothing on this page can recover from, where Retry would only fail the same way
const DEAD_END_ERRORS: CodeScannerErrorKind[] = ["no-camera", "insecure-context", "unsupported"]

interface SessionState {
    status: CodeScannerStatus
    error: CodeScannerErrorKind | null
    permission: CameraPermission
    // Keys the camera element, so bumping it starts a fresh camera session
    sessionId: number
    isHidden: boolean
    isPausedByCaller: boolean
}

type SessionAction =
    | { type: "decoderReady" }
    | { type: "visibility"; isHidden: boolean }
    | { type: "callerPause"; isPaused: boolean }
    | { type: "permission"; permission: CameraPermission }
    | { type: "failed"; error: CodeScannerErrorKind }
    | { type: "retry" }

function resolvedStatus(state: SessionState): CodeScannerStatus {
    if (state.error) {
        return "error"
    } else if (state.status === "preparing") {
        return "preparing"
    } else if (state.isHidden || state.isPausedByCaller) {
        return "paused"
    } else {
        return "scanning"
    }
}

function reduceSessionState(state: SessionState, action: SessionAction): SessionState {
    let next = state
    if (action.type === "decoderReady") {
        next = { ...state, status: "scanning" }
    } else if (action.type === "visibility") {
        next = { ...state, isHidden: action.isHidden }
    } else if (action.type === "callerPause") {
        next = { ...state, isPausedByCaller: action.isPaused }
    } else if (action.type === "permission") {
        next = { ...state, permission: action.permission }
    } else if (action.type === "failed") {
        next = { ...state, error: action.error }
    } else if (action.type === "retry" && state.error && !DEAD_END_ERRORS.includes(state.error)) {
        const status = state.error === "decoder-unavailable" ? "preparing" : state.status
        next = { ...state, status, error: null, sessionId: state.sessionId + 1 }
    }

    return { ...next, status: resolvedStatus(next) }
}

function errorKindFromScannerError(error: IScannerError): CodeScannerErrorKind {
    // The library reports its own start timeout as an unclassified error
    if (error.kind === "unknown" && error.message.includes("timed out")) {
        return "start-timeout"
    } else if (
        error.kind === "permission-denied" ||
        error.kind === "no-camera" ||
        error.kind === "in-use" ||
        error.kind === "insecure-context" ||
        error.kind === "unsupported"
    ) {
        return error.kind
    } else {
        return "unknown"
    }
}

function distanceFromCenter(code: IDetectedBarcode, video: HTMLVideoElement | null): number {
    const { x, y, width, height } = code.boundingBox
    const centerX = video ? video.videoWidth / 2 : 0
    const centerY = video ? video.videoHeight / 2 : 0

    return Math.hypot(x + width / 2 - centerX, y + height / 2 - centerY)
}

export interface UseCodeScannerSessionOptions {
    isPaused: boolean
}

export interface UseCodeScannerSessionReturn {
    status: CodeScannerStatus
    error: CodeScannerErrorKind | null
    permission: CameraPermission
    sessionId: number
    canRetry: boolean
    retry: () => void
    // Turns a frame's detections into at most one new code, or null when nothing new is in view
    acceptCodes: (codes: IDetectedBarcode[], video: HTMLVideoElement | null) => ScannedCode | null
    reportError: (error: IScannerError) => void
    reportInterruption: () => void
}

/**
 * Own the lifecycle around a camera scanning session: decoder readiness, camera permission, pausing while the tab is
 * hidden, per-code cooldowns, and recovery from camera failures.
 *
 * The camera element itself belongs to the component; this hook decides when it should run and which of its reads
 * count. Results that arrive after a session ended (a late frame, an error from a stopped camera) are dropped by
 * comparing against the session that is live now.
 */
export default function useCodeScannerSession(options: UseCodeScannerSessionOptions): UseCodeScannerSessionReturn {
    const { isPaused } = options
    const [state, dispatch] = useReducer(reduceSessionState, {
        status: "preparing",
        error: null,
        permission: "unknown",
        sessionId: 0,
        isHidden: typeof document !== "undefined" && document.visibilityState === "hidden",
        isPausedByCaller: isPaused,
    })
    const liveSessionRef = useRef(state.sessionId)
    const lastReadAtByCodeRef = useRef(new Map<string, number>())

    useEffect(() => {
        liveSessionRef.current = state.sessionId
        lastReadAtByCodeRef.current.clear()
    }, [state.sessionId])

    // Keyed on the session so Retry after a failed load tries the decoder again
    useEffect(() => {
        let isCurrent = true
        prepareCodeDecoder()
            .then(() => {
                if (isCurrent) {
                    dispatch({ type: "decoderReady" })
                }
            })
            .catch(() => {
                if (isCurrent) {
                    dispatch({ type: "failed", error: "decoder-unavailable" })
                }
            })

        return () => {
            isCurrent = false
        }
    }, [state.sessionId])

    useEffect(() => {
        dispatch({ type: "callerPause", isPaused })
    }, [isPaused])

    useEffect(() => {
        function handleVisibility() {
            dispatch({ type: "visibility", isHidden: document.visibilityState === "hidden" })
        }
        function handlePageHide() {
            dispatch({ type: "visibility", isHidden: true })
        }
        document.addEventListener("visibilitychange", handleVisibility)
        window.addEventListener("pagehide", handlePageHide)

        return () => {
            document.removeEventListener("visibilitychange", handleVisibility)
            window.removeEventListener("pagehide", handlePageHide)
        }
    }, [])

    useEffect(() => {
        // The permission query is missing or throws in some browsers (Firefox, older Safari); those stay "unknown"
        // and the camera request itself reports a denial
        let permissionStatus: PermissionStatus | null = null
        function handleChange() {
            if (permissionStatus) {
                dispatch({ type: "permission", permission: permissionStatus.state })
            }
        }
        navigator.permissions
            ?.query({ name: "camera" as PermissionName })
            .then((status) => {
                permissionStatus = status
                dispatch({ type: "permission", permission: status.state })
                status.addEventListener("change", handleChange)
            })
            .catch(() => {})

        return () => {
            permissionStatus?.removeEventListener("change", handleChange)
        }
    }, [])

    const acceptCodes = useCallback((codes: IDetectedBarcode[], video: HTMLVideoElement | null) => {
        // A monotonic clock: the cooldown must run even where the wall clock is frozen or jumps
        const now = performance.now()
        const lastReadAtByCode = lastReadAtByCodeRef.current
        // A pile of parts puts several codes in frame; the one the user is aiming at is the one in the middle
        const fresh = codes
            .filter(
                (code) =>
                    code.rawValue !== "" &&
                    now - (lastReadAtByCode.get(code.rawValue) ?? -Infinity) > SAME_CODE_COOLDOWN_MS,
            )
            .sort((codeA, codeB) => distanceFromCenter(codeA, video) - distanceFromCenter(codeB, video))
        if (fresh.length === 0) {
            return null
        }

        lastReadAtByCode.set(fresh[0].rawValue, now)

        return { value: fresh[0].rawValue, format: fresh[0].format }
    }, [])

    const reportError = useCallback((error: IScannerError) => {
        dispatch({ type: "failed", error: errorKindFromScannerError(error) })
    }, [])

    const reportInterruption = useCallback(() => {
        dispatch({ type: "failed", error: "interrupted" })
    }, [])

    const retry = useCallback(() => {
        dispatch({ type: "retry" })
    }, [])

    return {
        status: state.status,
        error: state.error,
        permission: state.permission,
        sessionId: state.sessionId,
        canRetry: state.error !== null && !DEAD_END_ERRORS.includes(state.error),
        retry,
        acceptCodes,
        reportError,
        reportInterruption,
    }
}
